/**
 * Leaderboard API utilities
 * Supports both local storage and remote API for leaderboard data
 * Includes offline queueing and automatic sync when API becomes available
 */

import axios from 'axios';
import { getPlayerId, getPlayerName } from './playerId';
import { getLeaderboardApiUrl } from './env';

export interface LeaderboardEntry {
  playerId: string;
  playerName: string;
  score: number;
  level: number;
  wordsFound: number;
  timestamp: number;
  isLocal?: boolean; // True if this is a local-only entry
}

export interface LeaderboardData {
  entries: LeaderboardEntry[];
  playerRank?: number; // Current player's rank (1-based)
}

const LEADERBOARD_STORAGE_KEY = 'letterweave_leaderboard_v1';
const PENDING_SUBMISSIONS_KEY = 'letterweave_pending_submissions_v1';
const MAX_LOCAL_ENTRIES = 100; // Keep top 100 local entries
const API_TIMEOUT_MS = 90000; // 90 seconds - allows time for Render to wake up from cold start (can take up to 60s)

// Get API URL dynamically (lazy evaluation to ensure window is available)
function getApiUrl(): string | undefined {
  const url = getLeaderboardApiUrl();
  if (typeof window !== 'undefined') {
    const windowAny = window as any;
    if (!windowAny.__leaderboard_api_logged__) {
      console.log('Leaderboard API URL:', url || 'Not configured (using local-only mode)');
      console.log('Current hostname:', window.location.hostname);
      windowAny.__leaderboard_api_logged__ = true;
      if (!url) {
        console.warn('⚠️ Leaderboard API not configured. Set REACT_APP_LEADERBOARD_API_URL to enable global leaderboard.');
      }
    }
  }
  return url;
}

// Track if we're currently syncing to avoid duplicate sync attempts
let isSyncing = false;
let syncRetryTimeout: NodeJS.Timeout | null = null;

/**
 * Check if a playerId is in UUID v4 format
 */
function isUUIDv4(playerId: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(playerId);
}

/**
 * Get leaderboard entries from local storage
 * Also migrates old playerId formats to current UUID
 */
function getLocalLeaderboard(): LeaderboardEntry[] {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.localStorage.getItem(LEADERBOARD_STORAGE_KEY);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      const currentPlayerId = getPlayerId();
      let needsUpdate = false;
      
      const entries = parsed.map(entry => {
        // Migrate old playerId format to current UUID
        if (entry.playerId && !isUUIDv4(entry.playerId)) {
          needsUpdate = true;
          return {
            ...entry,
            playerId: currentPlayerId,
            isLocal: true
          };
        }
        return {
          ...entry,
          isLocal: true
        };
      });
      
      // If we migrated entries, save them back
      if (needsUpdate) {
        try {
          const toStore = entries.map(({ isLocal, ...rest }) => rest);
          window.localStorage.setItem(LEADERBOARD_STORAGE_KEY, JSON.stringify(toStore));
          console.log('✅ Migrated old playerId formats in local leaderboard');
        } catch (error) {
          console.warn('Failed to save migrated entries:', error);
        }
      }
      
      return entries;
    }
  } catch (error) {
    console.warn('Failed to load local leaderboard:', error);
  }

  return [];
}

/**
 * Save leaderboard entry to local storage
 * Only keeps the best score per player (one entry per playerId)
 */
function saveLocalEntry(entry: LeaderboardEntry): void {
  if (typeof window === 'undefined') return;

  try {
    const entries = getLocalLeaderboard();
    
    // Find existing entry for this player
    const existingIndex = entries.findIndex(e => e.playerId === entry.playerId);
    
    if (existingIndex >= 0) {
      // Player already has an entry - only update if new score is higher
      const existing = entries[existingIndex];
      if (entry.score > existing.score) {
        // Replace with better score
        entries[existingIndex] = {
          ...entry,
          isLocal: true
        };
      }
      // If new score is lower or equal, ignore it (keep existing best)
    } else {
      // New player - add their entry
      entries.push({
        ...entry,
        isLocal: true
      });
    }
    
    // Sort by score (descending), then by timestamp (ascending for same score)
    entries.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return a.timestamp - b.timestamp;
    });
    
    // Keep only top entries
    const topEntries = entries.slice(0, MAX_LOCAL_ENTRIES);
    
    // Remove isLocal flag before storing (it's added on read)
    const toStore = topEntries.map(({ isLocal, ...rest }) => rest);
    window.localStorage.setItem(LEADERBOARD_STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    console.warn('Failed to save local leaderboard entry:', error);
  }
}

/**
 * Get pending submissions from localStorage
 */
function getPendingSubmissions(): LeaderboardEntry[] {
  if (typeof window === 'undefined') return [];
  
  try {
    const stored = window.localStorage.getItem(PENDING_SUBMISSIONS_KEY);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Failed to load pending submissions:', error);
    return [];
  }
}

/**
 * Save pending submissions to localStorage
 */
function savePendingSubmissions(submissions: LeaderboardEntry[]): void {
  if (typeof window === 'undefined') return;
  
  try {
    // Limit to 100 pending submissions to prevent storage bloat
    const limited = submissions.slice(0, 100);
    window.localStorage.setItem(PENDING_SUBMISSIONS_KEY, JSON.stringify(limited));
  } catch (error) {
    console.warn('Failed to save pending submissions:', error);
  }
}

/**
 * Add entry to pending submissions queue
 */
function addToPendingQueue(entry: LeaderboardEntry): void {
  const pending = getPendingSubmissions();
  
  // Check if this player already has a pending submission
  const existingIndex = pending.findIndex(e => e.playerId === entry.playerId);
  
  if (existingIndex >= 0) {
    // Only update if new score is higher
    if (entry.score > pending[existingIndex].score) {
      pending[existingIndex] = entry;
    }
  } else {
    pending.push(entry);
  }
  
  savePendingSubmissions(pending);
}

/**
 * Remove entry from pending queue
 */
function removeFromPendingQueue(playerId: string, timestamp: number): void {
  const pending = getPendingSubmissions();
  const filtered = pending.filter(
    e => !(e.playerId === playerId && e.timestamp === timestamp)
  );
  savePendingSubmissions(filtered);
}

/**
 * Sync pending submissions to API
 * Retries automatically when API becomes available
 */
async function syncPendingSubmissions(): Promise<void> {
  const apiUrl = getApiUrl();
  if (!apiUrl || isSyncing) {
    return;
  }
  
  const pending = getPendingSubmissions();
  if (pending.length === 0) {
    return;
  }
  
  isSyncing = true;
  
  try {
    // Try to submit each pending entry
    const syncPromises = pending.map(async (entry) => {
      try {
        await axios.post(apiUrl, entry, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: API_TIMEOUT_MS
        });
        
        // Success - remove from pending queue
        removeFromPendingQueue(entry.playerId, entry.timestamp);
        return true;
      } catch (error) {
        // Failed to sync this entry - keep it in queue
        console.warn('Failed to sync pending submission:', error);
        return false;
      }
    });
    
    await Promise.all(syncPromises);
    
    // Check if we successfully synced everything
    const remaining = getPendingSubmissions();
    if (remaining.length === 0) {
      console.log('All pending submissions synced successfully');
    } else {
      // Schedule retry in 30 seconds
      scheduleRetry();
    }
  } catch (error) {
    console.warn('Error during sync:', error);
    scheduleRetry();
  } finally {
    isSyncing = false;
  }
}

/**
 * Schedule a retry for syncing pending submissions
 */
function scheduleRetry(): void {
  if (syncRetryTimeout) {
    clearTimeout(syncRetryTimeout);
  }
  
  // Retry after 30 seconds
  syncRetryTimeout = setTimeout(() => {
    syncPendingSubmissions();
  }, 30000);
}

/**
 * Submit a score to the leaderboard (both local and remote if available)
 * If API is unreachable, queues for later sync
 */
export async function submitScore(
  score: number,
  level: number,
  wordsFound: number
): Promise<void> {
  const playerId = getPlayerId();
  const playerName = getPlayerName();
  
  const entry: LeaderboardEntry = {
    playerId,
    playerName,
    score,
    level,
    wordsFound,
    timestamp: Date.now()
  };

  // Always save locally first
  saveLocalEntry(entry);

  // Submit to remote API if configured
  const apiUrl = getApiUrl();
  if (apiUrl) {
    try {
      console.log('Submitting score to API:', apiUrl);
      await axios.post(apiUrl, entry, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: API_TIMEOUT_MS
      });
      
      console.log('✅ Score submitted successfully');
      // Success - try to sync any pending submissions
      syncPendingSubmissions();
    } catch (error: any) {
      // API unreachable - add to pending queue
      console.warn('❌ Failed to submit score to remote API, queuing for later:', error.message || error);
      if (error.response) {
        console.warn('API responded with status:', error.response.status);
      }
      addToPendingQueue(entry);
      
      // Schedule a retry
      scheduleRetry();
    }
  } else {
    console.log('📦 Score saved locally only (no API configured)');
  }
}

/**
 * Manually trigger sync of pending submissions
 * Can be called when user opens leaderboard or when coming back online
 */
export async function syncOfflineSubmissions(): Promise<void> {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
    // Offline - don't try to sync
    return;
  }
  
  await syncPendingSubmissions();
}

/**
 * Force sync all local leaderboard entries to the server
 * This pushes all local entries, not just pending submissions
 * Useful for manually syncing data that might have been missed
 * 
 * Note: For old entries with invalid playerIds, uses current playerId
 * For old timestamps, updates to current time to pass validation
 */
export async function forceSyncLocalData(): Promise<{ success: number; failed: number; errors: string[] }> {
  const apiUrl = getApiUrl();
  if (!apiUrl) {
    return { success: 0, failed: 0, errors: ['API URL not configured'] };
  }

  if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
    return { success: 0, failed: 0, errors: ['Device is offline'] };
  }

  const localEntries = getLocalLeaderboard();
  const results = { success: 0, failed: 0, errors: [] as string[] };
  const currentPlayerId = getPlayerId();
  const now = Date.now();
  const TIMESTAMP_TOLERANCE = 5 * 60 * 1000; // 5 minutes

  console.log(`🔄 Force syncing ${localEntries.length} local entries to server...`);

  for (let i = 0; i < localEntries.length; i++) {
    const entry = localEntries[i];
    
    // Add delay between submissions to avoid rate limiting (except for first one)
    if (i > 0) {
      await new Promise(resolve => setTimeout(resolve, 200)); // 200ms delay between requests
    }
    
    try {
      // Remove isLocal flag before sending
      const { isLocal, ...entryToSend } = entry;
      
      // Fix old playerId format - use current UUID if entry has old format
      if (!isUUIDv4(entryToSend.playerId)) {
        console.log(`⚠️ Migrating old playerId format for entry: ${entryToSend.playerName}`);
        entryToSend.playerId = currentPlayerId;
      }
      
      // For force sync, always update timestamps to be safe (1 hour ago)
      // This ensures they pass validation regardless of how old they are
      // The original timestamp is preserved in the local entry, but we use a safe timestamp for sync
      const SAFE_TIMESTAMP = now - (60 * 60 * 1000); // 1 hour ago (well within 7 day limit)
      if (entryToSend.timestamp < now - (5 * 60 * 1000)) {
        // Only update if more than 5 minutes old (to preserve recent timestamps)
        const hoursOld = Math.round((now - entryToSend.timestamp) / (60 * 60 * 1000));
        console.log(`⚠️ Updating old timestamp (${hoursOld}h old) to safe value for entry: ${entryToSend.playerName}`);
        entryToSend.timestamp = SAFE_TIMESTAMP;
      }
      
      await axios.post(apiUrl, entryToSend, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: API_TIMEOUT_MS
      });
      
      results.success++;
    } catch (error: any) {
      results.failed++;
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      results.errors.push(`${entry.playerName}: ${errorMsg}`);
      console.warn(`Failed to sync entry for ${entry.playerName}:`, errorMsg);
      
      // If rate limited, wait longer before continuing
      if (errorMsg.includes('Too many') || error.response?.status === 429) {
        console.log('⏳ Rate limited, waiting 3 seconds before continuing...');
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }
  }

  console.log(`✅ Force sync complete: ${results.success} successful, ${results.failed} failed`);
  
  // Also sync pending submissions
  await syncPendingSubmissions();

  return results;
}

/**
 * Update player name in all leaderboard entries (local and remote)
 * Updates the name for the current player's entries
 */
export async function updatePlayerNameInLeaderboard(newName: string): Promise<void> {
  const playerId = getPlayerId();
  
  // Update local entries
  const localEntries = getLocalLeaderboard();
  let updated = false;
  
  for (let i = 0; i < localEntries.length; i++) {
    if (localEntries[i].playerId === playerId) {
      localEntries[i].playerName = newName;
      updated = true;
    }
  }
  
  if (updated) {
    // Save updated local entries
    const toStore = localEntries.map(({ isLocal, ...rest }) => rest);
    try {
      window.localStorage.setItem(LEADERBOARD_STORAGE_KEY, JSON.stringify(toStore));
      console.log('✅ Updated player name in local leaderboard');
    } catch (error) {
      console.warn('Failed to update local leaderboard name:', error);
    }
  }
  
  // Update remote entry - resubmit the best score with new name
  // The backend will update the name even if the score is the same
  const apiUrl = getApiUrl();
  if (apiUrl) {
    const personalBest = getPersonalBest();
    if (personalBest) {
      // Resubmit the best score with the new name
      // The backend's addEntryToSupabase will update the name even if score is the same
      const entry: LeaderboardEntry = {
        ...personalBest,
        playerName: newName,
        timestamp: Date.now() // Update timestamp to ensure it passes validation
      };
      
      try {
        await axios.post(apiUrl, entry, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: API_TIMEOUT_MS
        });
        console.log('✅ Updated player name in remote leaderboard');
      } catch (error: any) {
        console.warn('Failed to update remote leaderboard name:', error.message || error);
        // Queue for later sync
        addToPendingQueue(entry);
      }
    }
  }
}

/**
 * Fetch leaderboard entries (from remote API if available, otherwise local)
 * Also triggers sync of pending submissions when API is available
 */
export async function fetchLeaderboard(limit: number = 50): Promise<LeaderboardData> {
  const playerId = getPlayerId();
  let entries: LeaderboardEntry[] = [];
  let apiAvailable = false;

  // Try to fetch from remote API first
  const apiUrl = getApiUrl();
  if (apiUrl) {
    try {
      console.log('Fetching leaderboard from:', apiUrl);
      const response = await axios.get(apiUrl, {
        params: { limit },
        timeout: API_TIMEOUT_MS
      });
      
      if (response.data && Array.isArray(response.data.entries)) {
        entries = response.data.entries.map((entry: any) => ({
          ...entry,
          isLocal: false // Mark as global (from API)
        }));
        apiAvailable = true;
        console.log(`✅ Fetched ${entries.length} entries from API`);
      } else if (Array.isArray(response.data)) {
        entries = response.data.map((entry: any) => ({
          ...entry,
          isLocal: false // Mark as global (from API)
        }));
        apiAvailable = true;
        console.log(`✅ Fetched ${entries.length} entries from API`);
      }
    } catch (error: any) {
      console.warn('❌ Failed to fetch remote leaderboard, using local:', error.message || error);
      if (error.response) {
        console.warn('API responded with status:', error.response.status);
      }
      // Fall through to local storage
    }
  } else {
    console.log('📦 Using local-only leaderboard (no API configured)');
  }

  // If API is available, try to sync pending submissions
  if (apiAvailable) {
    syncOfflineSubmissions();
  }

  // If no remote entries, use local storage
  if (entries.length === 0) {
    entries = getLocalLeaderboard();
  }

  // Deduplicate by playerId - only keep the best score per player
  const playerMap = new Map<string, LeaderboardEntry>();
  for (const entry of entries) {
    const existing = playerMap.get(entry.playerId);
    if (!existing || entry.score > existing.score) {
      playerMap.set(entry.playerId, entry);
    }
  }
  entries = Array.from(playerMap.values());

  // Sort and limit
  entries.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.timestamp - b.timestamp;
  });

  entries = entries.slice(0, limit);

  // Find player's rank
  const playerRank = entries.findIndex(entry => entry.playerId === playerId);
  const rank = playerRank >= 0 ? playerRank + 1 : undefined;

  return {
    entries,
    playerRank: rank
  };
}

/**
 * Get player's personal best score
 */
export function getPersonalBest(): LeaderboardEntry | null {
  const playerId = getPlayerId();
  const entries = getLocalLeaderboard();
  
  const playerEntries = entries
    .filter(entry => entry.playerId === playerId)
    .sort((a, b) => b.score - a.score);
  
  return playerEntries.length > 0 ? playerEntries[0] : null;
}

/**
 * Get player's best score for a specific level
 */
export function getPersonalBestForLevel(level: number): LeaderboardEntry | null {
  const playerId = getPlayerId();
  const entries = getLocalLeaderboard();
  
  const playerEntries = entries
    .filter(entry => entry.playerId === playerId && entry.level === level)
    .sort((a, b) => b.score - a.score);
  
  return playerEntries.length > 0 ? playerEntries[0] : null;
}

